import accountService from '../services/accountService'
import {externalScriptsService, postMessageService, storageService, staffService} from '@/modules/common/services'
import {urlUtil} from '@/modules/common/utils'
import {frontageApiService} from "@/plugins/frontage-api";
import {BusinessDateFormats} from '@/modules/common/filters'
import matterTermService from "@/modules/matters/services/matterTermService"
import {SmsSettingsService} from "@/modules/settings/services/SmsSettingsService";
import {settingsService} from "../../settings/services/settingsService";

const state = {
  profile: null,
  accountLoaded: false,
  loading: true,
  workAsStaff: null,
  teamsViewFilter: null,
  dateTimeFormats: null,
  staffMembers: null,
  channels: [],
  activeDedicatedNumber: null,
  integrationsSettings: {},
  businessSettings: null,
  businessTerm: {},
}

const getters = {
  account: state => state.profile,
  accountLoaded: state => !!state.profile,
  loading: state => state.loading,
  business: state => state.profile && state.profile.business,
  settings: state => state.profile &&  state.profile.settings,
  profile: state => state.profile,
  staff: state => state.workAsStaff,
  fields: state => state.profile && state.profile.client_fields,
  isStar: state => {
    const business = state.profile ? state.profile.business : null
    if (!business) return false
    const {whitelabel_configuration_key} = business
    return whitelabel_configuration_key && whitelabel_configuration_key.toLowerCase() === 'star'
  },
  isSpecialDirectory: state => {
    if (state.profile)
      return state.profile.whitelabel || (state.profile.business && state.profile.business.managed_co_branded)
    return false
  },
  hasAccessToSettings: state => {
    if (state.profile && state.profile.staff) {
      return state.profile.staff.role === 'admin' || state.profile.staff.role === 'manager' || state.profile.staff.role === 'marketing'
    } else {
      return state.profile.is_owner
    }
  },
  isCollaborator: state => {
    if (state.workAsStaff) {
      return state.workAsStaff.role === 'admin' || state.workAsStaff.role === 'manager' || state.workAsStaff.role === 'collaborator'
    } else {
      return state.profile && state.profile.is_owner
    }
  },
  teamsViewFilter: state => state.teamsViewFilter,
  hasPermission:
    state =>
      name =>
        state.workAsStaff &&
        state.workAsStaff.permissions[name] &&
        state.profile.staff &&
        state.profile.staff.permissions[name],
  isMultipleStaffs: (state, getters) => getters.business && getters.business.multistaff,
  pendo: state => state.profile && state.profile.external_integrations && state.profile.external_integrations.pendo,
  dateFormats: state => state.dateTimeFormats.date,
  timeFormats: state => state.dateTimeFormats.time,
  businessTerm: state => state.businessTerm,
  staffMembers: state => state.staffMembers,
  isExpired: state => state.profile.notifications.account_blocked,
  channels: state => state.channels,
  activeDedicatedNumber: state => state.activeDedicatedNumber,
  isMultiStaff: state => state.staffMembers.filter(member => member.active).length > 1,
  integrationsSettings: state => state.integrationsSettings,
  businessSettings: state => state.businessSettings,
  isOnlinePaymentsEnabled: state => state.profile && state.profile.settings && state.profile.settings.payments_enabled
}

const mutations = {
  setProfile(state, profile) {
    state.profile = profile
  },
  setLoading(state, value) {
    state.loading = value
  },
  setWorkAsStaff(state, value) {
    state.workAsStaff = value
  },
  setTeamsViewFilter(state, teamsViewFilter) {
    state.teamsViewFilter = teamsViewFilter
  },
  setDateTimeFormats(state, dateTimeFormats) {
    state.dateTimeFormats = dateTimeFormats
  },
  setBusinessTerm(state, value) {
    state.businessTerm = matterTermService.buildTerm(value)
  },
  setStaffMembers(state, value) {
    state.staffMembers = value
  },
  setChannels(state, channels) {
    state.channels = channels
  },
  setActiveDedicatedNumber(state, activeDedicatedNumber) {
    state.activeDedicatedNumber = activeDedicatedNumber
  },
  setIntegrationsSettings(state, integrationsSettings) {
    state.integrationsSettings = integrationsSettings
  },
  setBusinessSettings(state, businessSettings) {
    state.businessSettings = businessSettings
  },
  setStaffAttributes(state, Attributes) {
    Object.assign(state.profile.staff, Attributes)
  },
  setIsOnlinePaymentsEnabled(state, payments_enabled) {
    state.profile.settings.payments_enabled = payments_enabled
  }
}

const actions = {
  async fetchAccount({commit, dispatch, getters, rootGetters}) {
    try {
      const staffUid = urlUtil.getParameterByName("staffUid");
      commit('setLoading', true)
      let account = storageService.get(`account_${staffUid}`)

      if (!account || !account.profile || !account.workAsStaff) {
        const profile = await accountService.getAccount({full:true})
        const teams_view_filter = rootGetters.isMobileApp ? urlUtil.getParameterByName("teamsViewFilter") : await frontageApiService.get('teams_view_filter');
        account = {profile, workAsStaff: profile.staff, teams_view_filter}
      }

      const {profile, workAsStaff, teams_view_filter} = account
      commit('setProfile', profile)
      commit('setWorkAsStaff', workAsStaff)
      commit('setTeamsViewFilter', teams_view_filter)

      if (profile.constants.date_time_formats) {
        let date_time_formats = JSON.parse(profile.constants.date_time_formats)
        date_time_formats.date = JSON.parse(date_time_formats.date)
        date_time_formats.time = JSON.parse(date_time_formats.time)
        BusinessDateFormats.init(date_time_formats, profile.business.time_format)
        commit('setDateTimeFormats', date_time_formats)
      }

      commit({type: 'setFeatures', features: profile.business.features}, {root: true})

      dispatch({type: 'brandModule/initBrand', brand: profile.brand}, {root:true})

      commit('setBusinessTerm', profile.settings.selected_term);

      const {staff} = await staffService.getStaff();
      commit('setStaffMembers', staff);

      commit('setLoading', false)
      externalScriptsService.set(getters.account, {
        isStar: getters.isStar,
        isMobile: rootGetters.isMobile,
        hasFeature: rootGetters.hasFeature,
        isMobileApp: rootGetters.isMobileApp
      },
      {
        matterTerm: getters.businessTerm?.term,
        numOfStaff: getters.staffMembers.filter(member => member.active).length
      }
      )
      dispatch({type: 'accountModule/fetchIntegrationsSettings'}, {root:true})

    } catch (e) {
      console.error(e)
      commit('setLoading', false)
    }
  },
  async loadChannels({commit, state}, {payload}) {
    const res = await accountService.getChannels(payload.clientUid)

    if(res.success) {
      const channels = res.data.contact;
      channels.forEach(channel => {
        channel.value = channel.app_code_name // backward compatibility
        switch (channel.app_code_name) {
          case 'sms':
            channel.icon = 'icon-sms-message';
            break;
          case 'email':
            channel.icon = 'icon-Mail';
            break;
          case 'centralized_inbox':
          case 'webchat':
          case 'webchat_448':
            channel.logo = 'web-chat'
            channel.icon = 'icon-web-chat';
            break;
          case 'instagram':
          case 'instagram_463':
            channel.logo = 'instagram'
            channel.icon = 'icon-Apple_Business_Chat';
            break;
          case 'google':
            channel.logo = 'google'
            channel.icon = 'icon-Google-My-Business-Chat';
            break;
          case 'gbmprod':
          case 'gbm':
            channel.logo = 'google';
            channel.icon = 'icon-Google';
            break;
          case 'facebookmessenger':
          case 'facebookmessenger_447':
            channel.logo = 'messager'
            channel.icon = 'icon-FB-messager';
            break;
        }
      })
      commit('setChannels', channels)
    } else {
      commit('setChannels', {
        channel: 'email',
        value: 'email',
        channel_uid: 'dujrmr24hpmerwkr',
        order: 1,
        status: 'active',
        type: 'core'
      })
    }
  },
  async fetchActiveDedicatedNumber({commit}) {
    const activeDedicatedNumber = await SmsSettingsService.getBusinessActiveDedicatedNumber()
    commit('setActiveDedicatedNumber', activeDedicatedNumber)
  },
  async fetchIntegrationsSettings({commit}) {
    const integrationsSettings = await settingsService.getIntegrationsSettings()
    commit('setIntegrationsSettings', integrationsSettings)
  },
  async fetchBusinessSettings({state, commit}, {payload}) {
    const {businessId, forceFetch = false} = payload
    //AVOID duplicate server calls
    if (state.businessSettings && !forceFetch) return
    try {
      let settings = await settingsService.getBusinessSettings(businessId)
      commit('setBusinessSettings',settings)
    } catch (e) {
      console.log("fetchBusinessSettings", e)
    }
  },
  async updateOnlinePayments({commit}, {payments_enabled}) {
    try {
      await settingsService.update({payments_enabled})
      commit('setIsOnlinePaymentsEnabled', payments_enabled)
    } catch (e) {
      console.log("updateOnlinePayments", e)
    }
  },
  updateStaffAttributes({commit}, {payload}) {
    commit('setStaffAttributes', payload)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
